import { useEffect, useState } from 'react';
import type { HostingBackupCatalogView, HostingBackupSettingsView } from '@tosho/contracts';
import { formatBackupBytes } from './hosting-storage-format';
import { PrototypeIcon } from './PrototypeIcon';
import { backupDate, backupPresence, backupSchedule } from './HostingSiteBackups';

/** Relecture des projections locales seulement. Ouvrir une fiche n’appelle
 * ni Google ni Plesk ; la collecte durable travaille indépendamment de cet onglet. */
export function HostingBackupCatalogCard({
  serverId,
  siteId,
}: {
  readonly serverId: string;
  readonly siteId?: string;
}): React.JSX.Element {
  const [view, setView] = useState<HostingBackupCatalogView | null>(null),
    [error, setError] = useState(false);
  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    const read = async (): Promise<void> => {
      try {
        const query = siteId ? '?' + new URLSearchParams({ siteId }).toString() : '';
        const response = await fetch(
          `/api/hosting-servers/${encodeURIComponent(serverId)}/backup-catalog${query}`,
          { credentials: 'same-origin', cache: 'no-store', signal: controller.signal },
        );
        if (!response.ok) throw new Error();
        const value: unknown = await response.json();
        if (!isCatalog(value)) throw new Error();
        if (active) {
          setView(value);
          setError(false);
        }
      } catch {
        if (active) setError(true);
      }
    };
    void read();
    const timer = window.setInterval(() => void read(), 15000);
    return () => {
      active = false;
      controller.abort();
      window.clearInterval(timer);
    };
  }, [serverId, siteId]);
  return (
    <section
      className="hosting-backup-catalog"
      aria-label={siteId ? 'Sauvegardes du vhost' : 'Sauvegarde générale du serveur'}
    >
      <header>
        <div>
          <p className="dashboard-panel__eyebrow">Suivi automatique</p>
          <h3>
            <PrototypeIcon name="database" />
            {siteId ? 'Sauvegardes du vhost' : 'Sauvegarde générale du serveur'}
          </h3>
        </div>
        {view?.folderUrl && (
          <a href={view.folderUrl} target="_blank" rel="noreferrer">
            Voir le dossier Drive
          </a>
        )}
      </header>
      {error && (
        <p className="tosho-feedback is-error" role="alert">
          Le relevé enregistré ne peut pas être chargé. La dernière lecture affichée est conservée.
        </p>
      )}
      {view ? (
        <>
          <dl>
            <div>
              <dt>
                {view.remote?.hasUnverifiedCopies
                  ? 'Dernière sauvegarde confirmée'
                  : 'Dernière sauvegarde'}
              </dt>
              <dd>
                {view.remote?.recent[0]
                  ? backupDate(view.remote.recent[0].pointKey)
                  : view.remote?.presence === 'EMPTY'
                    ? 'Aucune copie sur Google Drive'
                    : 'Date inconnue'}
              </dd>
            </div>
            <div>
              <dt>Google Drive</dt>
              <dd>{backupPresence(view.remote)}</dd>
            </div>
            <div>
              <dt>Programmation Plesk</dt>
              <dd>{backupSchedule(view.remote?.schedule, view.scope)}</dd>
            </div>
          </dl>
          {view.remote?.presence === 'UNKNOWN' && view.pleskError && (
            <p role="status">
              Plesk n’a pas pu lire les copies Google Drive. Vérifiez la destination Google Drive
              dans le gestionnaire de sauvegardes Plesk.
            </p>
          )}
          {view.remote?.hasUnverifiedCopies && (
            <p>
              Des copies restent à vérifier dans Plesk. Les copies signalées comme réussies restent
              visibles.
            </p>
          )}
          {!!view.remote?.recent.length && (
            <>
              <h4>Les 5 dernières sauvegardes Google Drive</h4>
              <div className="hosting-backup-catalog__table">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Type</th>
                      <th>Taille</th>
                    </tr>
                  </thead>
                  <tbody>
                    {view.remote.recent.slice(0, 5).map((archive, index) => (
                      <tr key={archive.pointKey + '-' + index}>
                        <td>{backupDate(archive.pointKey)}</td>
                        <td>{archive.kind === 'BASE' ? 'Complète' : 'Incrémentale'}</td>
                        <td>{formatBackupBytes(archive.sizeBytes)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {!view.remote.isCurrent && (
                <p>Dernières copies connues ; leur présence actuelle reste à vérifier.</p>
              )}
            </>
          )}
          {view.remote?.observedAt && <small>Vérifié le {date(view.remote.observedAt)}</small>}
        </>
      ) : (
        !error && <p role="status">Chargement du relevé…</p>
      )}
      <p className="hosting-backup-catalog__notice">
        Les sauvegardes et les restaurations restent gérées dans Plesk. Les dates et horaires
        affichés sont ceux du serveur Plesk.
      </p>
    </section>
  );
}
function isCatalog(value: unknown): value is HostingBackupCatalogView {
  if (
    !value ||
    typeof value !== 'object' ||
    !('scope' in value) ||
    !['SERVER', 'VHOST'].includes(value.scope as string) ||
    !('scopeName' in value) ||
    typeof value.scopeName !== 'string' ||
    !('totalSizeBytes' in value) ||
    (value.totalSizeBytes !== null &&
      (typeof value.totalSizeBytes !== 'string' || !/^[0-9]{1,24}$/u.test(value.totalSizeBytes)))
  )
    return false;
  return (
    'archives' in value &&
    Array.isArray(value.archives) &&
    'chains' in value &&
    Array.isArray(value.chains) &&
    value.archives.every(
      (row: unknown) =>
        !!row &&
        typeof row === 'object' &&
        'name' in row &&
        typeof row.name === 'string' &&
        'sizeBytes' in row &&
        (row.sizeBytes === null ||
          (typeof row.sizeBytes === 'string' && /^[0-9]{1,20}$/u.test(row.sizeBytes))),
    )
  );
}
function date(value: string): string {
  return new Date(value).toLocaleString('fr-FR');
}

export function HostingBackupSettingsCard({
  csrfToken,
  enabled,
  onReauthenticate,
}: {
  readonly csrfToken: string;
  readonly enabled: boolean;
  readonly onReauthenticate: () => void;
}): React.JSX.Element {
  const [view, setView] = useState<HostingBackupSettingsView | null>(null),
    [url, setUrl] = useState(''),
    [collect, setCollect] = useState(true),
    [busy, setBusy] = useState(false),
    [message, setMessage] = useState<string | null>(null);
  const [messageSucceeded, setMessageSucceeded] = useState(false);
  useEffect(() => {
    let active = true;
    void fetch('/api/hosting-backups/settings', { credentials: 'same-origin', cache: 'no-store' })
      .then(async (response) => {
        if (!response.ok) throw new Error();
        return (await response.json()) as HostingBackupSettingsView;
      })
      .then((value) => {
        if (active) {
          setView(value);
          setUrl(value.rootFolderUrl ?? '');
          setCollect(value.revision === null ? true : value.enabled);
        }
      })
      .catch(() => {
        if (active) setMessage('Les réglages de collecte ne peuvent pas être chargés.');
      });
    return () => {
      active = false;
    };
  }, []);
  async function save(): Promise<void> {
    if (busy || !view) return;
    setBusy(true);
    setMessage(null);
    setMessageSucceeded(false);
    try {
      const response = await fetch('/api/hosting-backups/settings', {
        method: 'PUT',
        credentials: 'same-origin',
        cache: 'no-store',
        headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrfToken },
        body: JSON.stringify({
          expectedRevision: view.revision,
          rootFolderUrl: url.trim(),
          enabled: collect,
        }),
      });
      if (!response.ok) {
        const body = (await response.json()) as { code?: string };
        if (body.code === 'REAUTHENTICATION_REQUIRED') {
          onReauthenticate();
          return;
        }
        if (body.code === 'HOSTING_BACKUP_SETTINGS_CONFLICT')
          setMessage('Ces réglages ont changé. Rechargez la page avant de les enregistrer.');
        else
          setMessage(
            'La collecte ne peut pas être configurée. Vérifiez l’autorisation et le lien du dossier.',
          );
        return;
      }
      const saved = (await response.json()) as HostingBackupSettingsView;
      setMessageSucceeded(true);
      setView(saved);
      setUrl(saved.rootFolderUrl ?? '');
      setMessage(
        saved.enabled
          ? 'Collecte automatique enregistrée. Les premiers relevés apparaîtront progressivement.'
          : 'Collecte suspendue. Les relevés précédents sont conservés.',
      );
    } catch {
      setMessage(
        'L’enregistrement ne peut pas être confirmé. Rechargez les réglages avant de réessayer.',
      );
    } finally {
      setBusy(false);
    }
  }
  return (
    <div className="hosting-backup-settings">
      <label>
        Dossier racine des sauvegardes (Clients)
        <input
          type="url"
          value={url}
          onChange={(event) => setUrl(event.target.value)}
          placeholder="https://drive.google.com/drive/folders/…"
          disabled={busy}
        />
      </label>
      <p>
        Les dossiers des vhosts doivent porter leur nom de domaine exact. Le dossier d’un serveur
        porte son libellé dans Tosho. Les deux niveaux sont lus séparément.
      </p>
      <label className="hosting-backup-settings__toggle">
        <input
          type="checkbox"
          checked={collect}
          onChange={(event) => setCollect(event.target.checked)}
          disabled={busy || (!enabled && !collect)}
        />
        Collecter automatiquement les métadonnées chaque heure
      </label>
      {view?.enabled && !view.authorizationIsCurrent && (
        <p role="status">
          L’autorisation a changé. Reconnectez le même Drive puis enregistrez à nouveau la collecte.
        </p>
      )}
      <button
        type="button"
        disabled={busy || !view || (!enabled && collect) || !url.trim()}
        onClick={() => void save()}
      >
        {busy ? 'Enregistrement…' : 'Enregistrer la collecte'}
      </button>
      {message && (
        <p
          role="status"
          className={'tosho-feedback ' + (messageSucceeded ? 'is-success' : 'is-error')}
        >
          {message}
        </p>
      )}
    </div>
  );
}
